import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Bus, Car, Loader2, RefreshCw, Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import type {
  DisruptionTripsPage,
  ITransferBoard,
  TransferLeg,
} from '@/lib/disruptions/types';
import {
  useDispatchCars,
  useDispatchTransfers,
  usePlanTransfers,
} from '@/lib/disruptions/use-disruptions';
import { shortTime } from '../tower/tower-model';
import { boardTotals, stripsOf } from '../tower/tower-transfer-board';
import { TransferPanel } from '../tower/tower-transfer-panel';
import { CarsStrip } from './cars-strip';
import { TransportGoalBar } from './transport-goal-bar';
import { useTransportDesk } from './use-transport-desk';
import type { TransportDeskState } from './transport-desk-model';

// Disruption "Transport" tab — the desk grammar over ground transport (NEX-463):
//   goal bar (ONE classifier's lanes) → mass bar (scope sentence + status line +
//   the wave primaries, swapping to per-passenger cars below the threshold) →
//   sub-tab row (Runs board · Exceptions · Journeys) → the RUNS BOARD, which is
//   the EXISTING Tower TransferPanel re-homed (single source — moved, not
//   copied) plus a leg lens and the collapsed "Cars & on-demand" strip.
// T4 (NEX-464) adds the RUN SHEET drawer (per-run manifest + kerb verbs), the
// per-hotel Add-vehicle, and the real EXCEPTIONS queue (two-pane inbox).
// Realtime = WS: board + trips seeded once, patched by apply-event; NO polling.
// Deep links ride the hash:
//   `#transport?hotel=<urn>&leg=out|return|both&run=<runUrn>&pnr=<subCaseUrn>`.

// Code-split the heavy T4/T7 surfaces: the run sheet + the exceptions queue +
// the journeys view mount on demand, so the index chunk doesn't pay for a desk
// the operator hasn't opened.
const RunSheet = lazy(() => import('./run-sheet'));
const ExceptionsQueue = lazy(() => import('./exceptions-queue'));
const AddVehicleDialog = lazy(() => import('./add-vehicle-dialog'));
const JourneysView = lazy(() => import('./journeys-view'));

export type TransportLegLens = 'OUT' | 'RETURN' | 'BOTH';
type TransportSubTab = 'runs' | 'exceptions' | 'journeys';

const LEG_VALUES: ReadonlySet<string> = new Set(['out', 'return', 'both']);

/** Parse the tab's hash grammar. Params only apply when the hash IS `#transport`. */
export function parseTransportHash(hash: string): {
  focusHotel?: string;
  leg?: TransportLegLens;
  /** Run-sheet deep link — the run URN to open. */
  run?: string;
  /** Exceptions deep link — the sub-case to pre-select in the queue. */
  pnr?: string;
} {
  const [view = '', query = ''] = hash.replace(/^#/, '').split('?');
  if (view !== 'transport') return {};
  const params = new URLSearchParams(query);
  const hotel = params.get('hotel');
  const leg = params.get('leg')?.toLowerCase();
  const run = params.get('run');
  const pnr = params.get('pnr');
  return {
    ...(hotel ? { focusHotel: hotel } : {}),
    ...(leg && LEG_VALUES.has(leg) ? { leg: leg.toUpperCase() as TransportLegLens } : {}),
    ...(run ? { run } : {}),
    ...(pnr ? { pnr } : {}),
  };
}

export function TransportDeskTab({
  disruptionUrn,
  enabled = true,
}: {
  disruptionUrn: string;
  enabled?: boolean;
}) {
  const { t } = useTranslation('transport');
  const { hash } = useLocation();
  const navigate = useNavigate();
  const deepLink = useMemo(() => parseTransportHash(hash), [hash]);
  const q = useTransportDesk(disruptionUrn, enabled);
  // One strip map PER LEG — the "Both legs" view draws two boards over the same
  // read, and a single (outbound) map made the return board repeat the outbound's
  // pax / vehicle / no-show counts.
  const strips = useMemo(() => stripsOf(q.board, 'AIRPORT_TO_HOTEL'), [q.board]);
  const returnStrips = useMemo(() => stripsOf(q.board, 'HOTEL_TO_AIRPORT'), [q.board]);
  const totals = useMemo(() => boardTotals(strips.values()), [strips]);

  // A ?pnr= deep link lands on the Exceptions queue with that row selected.
  const [sub, setSub] = useState<TransportSubTab>(deepLink.pnr ? 'exceptions' : 'runs');
  const [leg, setLeg] = useState<TransportLegLens>(deepLink.leg ?? 'OUT');
  useEffect(() => {
    if (deepLink.leg) setLeg(deepLink.leg);
  }, [deepLink.leg]);
  useEffect(() => {
    if (deepLink.pnr) setSub('exceptions');
  }, [deepLink.pnr]);

  // The run sheet: opened from a run row OR the ?run= deep link. Closing a
  // deep-linked sheet also cleans the hash so the tab doesn't re-open it.
  const [runSheetUrn, setRunSheetUrn] = useState<string | null>(deepLink.run ?? null);
  useEffect(() => {
    if (deepLink.run) setRunSheetUrn(deepLink.run);
  }, [deepLink.run]);
  const openRun = useMemo(
    () => (runSheetUrn ? q.board?.runs.find((r) => r.urn === runSheetUrn) : undefined),
    [runSheetUrn, q.board],
  );
  const closeRunSheet = (): void => {
    setRunSheetUrn(null);
    if (deepLink.run) navigate({ hash: '#transport' }, { replace: true });
  };

  // Per-hotel "Add vehicle…" (panel footer) — hotel preset, leg from the lens.
  const [addVehicle, setAddVehicle] = useState<{ hotelUrn: string; leg: TransferLeg } | null>(null);

  const belowThreshold = q.board?.facts?.belowThreshold === true;
  const fixtures = q.board?.source === 'fixtures' || q.trips?.source === 'fixtures';

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-3 sm:p-4" data-testid="transport-desk-tab">
      {q.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : q.isError ? (
        <EmptyState
          title={t('tab.rosterError.title')}
          description={t('tab.rosterError.description')}
        />
      ) : q.desk ? (
        <TransportGoalBar desk={q.desk} truncated={q.truncated} />
      ) : null}

      <MassBar
        disruptionUrn={disruptionUrn}
        board={q.board}
        totals={totals}
        belowThreshold={belowThreshold}
        fixtures={fixtures}
        groundAirportIata={q.desk?.groundAirportIata ?? null}
      />

      {/* Sub-tab row — Runs board leads; Exceptions/Journeys land with T4/T7. */}
      <div className="flex items-center gap-1" role="tablist" aria-label={t('tab.subTabs.aria')}>
        <SubTab active={sub === 'runs'} onClick={() => setSub('runs')} testId="transport-sub-runs">
          {t('tab.subTabs.runs')}
        </SubTab>
        <SubTab
          active={sub === 'exceptions'}
          onClick={() => setSub('exceptions')}
          testId="transport-sub-exceptions"
        >
          {t('tab.subTabs.exceptions')}
          {q.desk && q.desk.queue.length > 0 ? (
            <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold tabular-nums text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              {q.desk.queue.length}
            </span>
          ) : null}
        </SubTab>
        <SubTab
          active={sub === 'journeys'}
          onClick={() => setSub('journeys')}
          testId="transport-sub-journeys"
        >
          {t('tab.subTabs.journeys')}
        </SubTab>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {sub === 'exceptions' ? (
          q.isLoading ? (
            <Skeleton className="h-24 w-full" data-testid="transport-exceptions-loading" />
          ) : q.desk ? (
            <Suspense fallback={<Skeleton className="h-24 w-full" />}>
              <ExceptionsQueue
                disruptionUrn={disruptionUrn}
                desk={q.desk}
                board={q.board}
                preselect={deepLink.pnr}
              />
            </Suspense>
          ) : (
            <EmptyState
              title={t('tab.rosterError.title')}
              description={t('tab.rosterError.description')}
            />
          )
        ) : sub === 'journeys' ? (
          <Suspense fallback={<Skeleton className="h-24 w-full" />}>
            <JourneysView
              disruptionUrn={disruptionUrn}
              rosterRows={q.desk?.rows.map((d) => d.row) ?? []}
              rosterLoading={q.isLoading}
              rosterError={q.isError}
              board={q.board}
              trips={q.trips}
              tripsLoading={q.tripsLoading || q.boardLoading}
              onRetry={q.refetch}
              groundAirportIata={q.desk?.groundAirportIata ?? null}
            />
          </Suspense>
        ) : (
          <RunsBoard
            disruptionUrn={disruptionUrn}
            desk={q.desk}
            board={q.board}
            boardLoading={q.boardLoading}
            boardError={q.boardError}
            strips={strips}
            returnStrips={returnStrips}
            leg={leg}
            onLeg={setLeg}
            focusHotel={deepLink.focusHotel}
            belowThreshold={belowThreshold}
            tripsPage={q.trips}
            tripsLoading={q.tripsLoading}
            onOpenRun={(run) => setRunSheetUrn(run.urn)}
            onAddVehicle={(hotelUrn, runLeg) => setAddVehicle({ hotelUrn, leg: runLeg })}
          />
        )}
      </div>

      {openRun ? (
        <Suspense fallback={null}>
          <RunSheet
            disruptionUrn={disruptionUrn}
            run={openRun}
            board={q.board}
            rosterRows={q.desk?.rows.map((d) => d.row) ?? []}
            groundAirportIata={q.desk?.groundAirportIata ?? null}
            onClose={closeRunSheet}
          />
        </Suspense>
      ) : runSheetUrn && q.board ? (
        // A deep-linked run the board doesn't know — say so instead of a blank.
        <Dialog open onOpenChange={(open) => (!open ? closeRunSheet() : undefined)}>
          <DialogContent className="max-w-md" data-testid="run-sheet-not-found">
            <DialogHeader>
              <DialogTitle>{t('runSheet.notFound.title')}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">{t('runSheet.notFound.body')}</p>
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={closeRunSheet}>
                {t('actions.close')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}

      {addVehicle ? (
        <Suspense fallback={null}>
          <AddVehicleDialog
            disruptionUrn={disruptionUrn}
            board={q.board}
            hotelUrn={addVehicle.hotelUrn}
            leg={addVehicle.leg}
            onClose={() => setAddVehicle(null)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}

function SubTab({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'rounded-md px-3 py-1 text-sm transition-colors',
        active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

/** Scope sentence + live status line + the wave primaries. Below the tenant's
 *  mass threshold the primary swaps to per-passenger cars (reason dialog →
 *  POST dispatch-cars); the shuttle lever stays one step behind as an outline. */
function MassBar({
  disruptionUrn,
  board,
  totals,
  belowThreshold,
  fixtures,
  groundAirportIata,
}: {
  disruptionUrn: string;
  board: ITransferBoard | null | undefined;
  totals: ReturnType<typeof boardTotals>;
  belowThreshold: boolean;
  fixtures: boolean;
  /** Issue #662: Airport for formatting ground times in the correct timezone. */
  groundAirportIata: string | null;
}) {
  const { t } = useTranslation('transport');
  const plan = usePlanTransfers(disruptionUrn);
  const dispatch = useDispatchTransfers(disruptionUrn);
  const [carsOpen, setCarsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busy = plan.isPending || dispatch.isPending;
  const hasPlan = !!board?.plan;

  const statusLine = useMemo(() => {
    if (!board) return null;
    if (totals.failed > 0) {
      return {
        tone: 'warn' as const,
        text: t('mass.status.failed', { count: totals.failed }),
      };
    }
    if (belowThreshold) {
      return { tone: 'muted' as const, text: t('mass.status.belowThreshold') };
    }
    const outbound = board.runs.filter((r) => r.leg === 'AIRPORT_TO_HOTEL');
    if (outbound.length > 0) {
      const wave = Math.max(...outbound.map((r) => r.wave || 1));
      const next = outbound
        .filter((r) => r.status === 'PLANNED' || r.status === 'REQUESTED' || r.status === 'VEHICLE_ASSIGNED' || r.status === 'BOARDING')
        .map((r) => r.scheduledAt)
        .filter(Boolean)
        .sort()[0];
      return {
        tone: 'muted' as const,
        text: t(next ? 'mass.status.waveNext' : 'mass.status.wave', {
          wave,
          arrived: totals.paxArrived.toLocaleString(),
          onRoad: totals.paxOnRoad.toLocaleString(),
          ...(next ? { time: shortTime(next, groundAirportIata) } : {}),
        }),
      };
    }
    if (hasPlan) return { tone: 'muted' as const, text: t('mass.status.cut') };
    return { tone: 'muted' as const, text: t('mass.status.noPlan') };
  }, [board, totals, hasPlan, belowThreshold, t]);

  const fail = (key: string) => (err: Error) => setError(t(key, { message: err.message }));

  return (
    <section
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-card px-3 py-2"
      data-testid="transport-mass-bar"
    >
      <Bus className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <div className="min-w-0">
        <p className="text-sm font-medium">{t('mass.title')}</p>
        {statusLine ? (
          <p
            className={cn(
              'truncate text-xs',
              statusLine.tone === 'warn'
                ? 'text-amber-700 dark:text-amber-300'
                : 'text-muted-foreground',
            )}
            data-testid="transport-mass-status"
          >
            {statusLine.text}
          </p>
        ) : null}
      </div>
      {fixtures ? (
        <Badge variant="neutral" size="sm" data-testid="transport-fixtures-badge">
          {t('mass.fixtures')}
        </Badge>
      ) : null}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {belowThreshold ? (
          <>
            <Button
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              onClick={() => setCarsOpen(true)}
              data-testid="transport-dispatch-cars"
              title={t('mass.dispatchCars.tooltip')}
            >
              <Car className="h-3 w-3" aria-hidden /> {t('mass.dispatchCars.label')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 px-2 text-xs"
              disabled={busy || !board}
              onClick={() => {
                setError(null);
                plan.mutate(undefined, { onError: fail('mass.error.plan') });
              }}
              data-testid="transport-plan-all"
              title={t('mass.plan.tooltipBelow')}
            >
              {plan.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <RefreshCw className="h-3 w-3" aria-hidden />}
              {t(hasPlan ? 'actions.recutAll' : 'actions.planShuttles')}
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant={hasPlan ? 'outline' : 'default'}
              className="h-7 gap-1 px-2 text-xs"
              disabled={busy || !board}
              onClick={() => {
                setError(null);
                plan.mutate(undefined, { onError: fail('mass.error.plan') });
              }}
              data-testid="transport-plan-all"
              title={t(hasPlan ? 'mass.plan.tooltipRecut' : 'mass.plan.tooltipPlan')}
            >
              {plan.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <RefreshCw className="h-3 w-3" aria-hidden />}
              {t(hasPlan ? 'actions.recutAll' : 'actions.planShuttles')}
            </Button>
            {hasPlan ? (
              <Button
                size="sm"
                className="h-7 gap-1 px-2 text-xs"
                disabled={busy || totals.paxWaiting === 0}
                onClick={() => {
                  setError(null);
                  dispatch.mutate(
                    { reason: 'Mass transfer to hotels (Transport desk)' },
                    { onError: fail('mass.error.dispatch') },
                  );
                }}
                data-testid="transport-send-all"
                title={t('mass.sendAll.tooltip')}
              >
                {dispatch.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Send className="h-3 w-3" aria-hidden />}
                {t('mass.sendAll.label', { pax: totals.paxWaiting.toLocaleString() })}
              </Button>
            ) : null}
          </>
        )}
      </div>
      {error ? (
        <p className="w-full text-xs text-destructive" data-testid="transport-mass-error">
          {error}
        </p>
      ) : null}
      {carsOpen ? (
        <DispatchCarsDialog disruptionUrn={disruptionUrn} onClose={() => setCarsOpen(false)} />
      ) : null}
    </section>
  );
}

/** The below-threshold mass CAR fan-out needs an operator reason (audit trail). */
function DispatchCarsDialog({
  disruptionUrn,
  onClose,
}: {
  disruptionUrn: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('transport');
  const cars = useDispatchCars(disruptionUrn);
  const [reason, setReason] = useState('');

  const confirm = async (): Promise<void> => {
    try {
      await cars.mutateAsync({ reason: reason.trim() });
    } catch {
      // Mutation state renders the error inline; the dialog stays open to retry.
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('mass.dispatchCars.label')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t('mass.dispatchCars.body')}</p>
        <label className="block text-xs text-muted-foreground">
          {t('fields.reasonRequired')}
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
            placeholder={t('mass.dispatchCars.reasonPlaceholder')}
            data-testid="dispatch-cars-reason"
          />
        </label>
        {cars.isError ? (
          <p className="text-xs text-destructive" data-testid="dispatch-cars-error">
            {t('mass.dispatchCars.error')}
          </p>
        ) : null}
        {cars.data ? (
          <p className="text-xs text-muted-foreground" data-testid="dispatch-cars-result">
            {t('mass.dispatchCars.result', {
              dispatched: cars.data.dispatched,
              skipped: cars.data.skipped,
            })}
            {cars.data.failed.length > 0 ? (
              <span className="text-destructive">
                {' '}
                · {t('mass.dispatchCars.failedCount', { count: cars.data.failed.length })}
              </span>
            ) : null}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={cars.isPending}>
            {cars.data ? t('actions.close') : t('common:common.cancel')}
          </Button>
          {!cars.data ? (
            <Button
              onClick={() => void confirm()}
              disabled={cars.isPending || reason.trim().length === 0}
              data-testid="dispatch-cars-confirm"
            >
              {cars.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
              {t('mass.dispatchCars.confirm')}
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The runs board: leg lens (never interleaves legs) + the re-homed TransferPanel
 *  + the collapsed cars strip. The panel stays the single source of run rows. */
function RunsBoard({
  disruptionUrn,
  desk,
  board,
  boardLoading,
  boardError,
  strips,
  returnStrips,
  leg,
  onLeg,
  focusHotel,
  belowThreshold,
  tripsPage,
  tripsLoading,
  onOpenRun,
  onAddVehicle,
}: {
  disruptionUrn: string;
  desk: TransportDeskState | null;
  board: ITransferBoard | null | undefined;
  boardLoading: boolean;
  boardError: boolean;
  strips: ReturnType<typeof stripsOf>;
  returnStrips: ReturnType<typeof stripsOf>;
  leg: TransportLegLens;
  onLeg: (l: TransportLegLens) => void;
  focusHotel: string | undefined;
  belowThreshold: boolean;
  tripsPage: DisruptionTripsPage | null | undefined;
  tripsLoading: boolean;
  onOpenRun: NonNullable<Parameters<typeof TransferPanel>[0]['onOpenRun']>;
  onAddVehicle: (hotelUrn: string, leg: TransferLeg) => void;
}) {
  const { t } = useTranslation('transport');
  const plan = usePlanTransfers(disruptionUrn);
  const cold = !!board && !board.plan && board.runs.length === 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1" role="tablist" aria-label={t('runs.legLens.aria')}>
        {(
          [
            ['OUT', 'runs.leg.out'],
            ['RETURN', 'runs.leg.return'],
            ['BOTH', 'runs.leg.both'],
          ] as const
        ).map(([value, labelKey]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={leg === value}
            onClick={() => onLeg(value)}
            data-testid={`transport-leg-${value.toLowerCase()}`}
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
              leg === value
                ? 'border-primary bg-primary/10 font-semibold text-foreground'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>

      {cold && !belowThreshold ? (
        <EmptyState
          icon={<Bus className="h-6 w-6" aria-hidden />}
          title={t('runs.cold.title')}
          description={t('runs.cold.description')}
          action={
            <Button
              onClick={() => plan.mutate()}
              disabled={plan.isPending}
              data-testid="transport-cold-plan"
            >
              {plan.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
              {t('actions.planShuttles')}
            </Button>
          }
          className="py-10"
        />
      ) : (
        <>
          {belowThreshold && cold ? (
            <p
              className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
              data-testid="transport-below-threshold-note"
            >
              {t('runs.belowThresholdNote')}
            </p>
          ) : null}
          {(leg === 'OUT' || leg === 'BOTH') && !(belowThreshold && cold) ? (
            <div data-testid="transport-panel-outbound">
              {leg === 'BOTH' ? <LegCaption>{t('runs.caption.outbound')}</LegCaption> : null}
              <TransferPanel
                disruptionUrn={disruptionUrn}
                board={board}
                isLoading={boardLoading}
                isError={boardError}
                strips={strips}
                focusHotel={focusHotel}
                headerActions={false}
                onOpenRun={onOpenRun}
                onAddVehicle={(hotelUrn) => onAddVehicle(hotelUrn, 'AIRPORT_TO_HOTEL')}
                groundAirportIata={desk?.groundAirportIata ?? null}
              />
            </div>
          ) : null}
          {(leg === 'RETURN' || leg === 'BOTH') && !(belowThreshold && cold) ? (
            <div data-testid="transport-panel-return">
              {leg === 'BOTH' ? <LegCaption>{t('runs.caption.return')}</LegCaption> : null}
              <TransferPanel
                disruptionUrn={disruptionUrn}
                board={board}
                isLoading={boardLoading}
                isError={boardError}
                strips={returnStrips}
                lens="RETURN"
                focusHotel={leg === 'RETURN' ? focusHotel : undefined}
                headerActions={false}
                onOpenRun={onOpenRun}
                onAddVehicle={(hotelUrn) => onAddVehicle(hotelUrn, 'HOTEL_TO_AIRPORT')}
                groundAirportIata={desk?.groundAirportIata ?? null}
              />
            </div>
          ) : null}
        </>
      )}

      <CarsStrip page={tripsPage} isLoading={tripsLoading} defaultOpen={belowThreshold} groundAirportIata={desk?.groundAirportIata ?? null} />

      {desk?.allSettled ? (
        <p className="text-center text-xs text-muted-foreground" data-testid="transport-calm-note">
          {t('runs.calm')}
        </p>
      ) : null}
    </div>
  );
}

function LegCaption({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      {children}
    </p>
  );
}
